import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { supabase, fmtBRL } from "@/lib/supabase";
import { db } from "@/lib/db";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PenLine, Check, X, FileSignature, Sparkles } from "lucide-react";
import { toast } from "sonner";

export default function PublicProposal() {
  const { id } = useParams();
  const [p, setP] = useState(null);
  const [loading, setLoading] = useState(true);
  const [acted, setActed] = useState(false);
  const [showContract, setShowContract] = useState(false);
  const [signing, setSigning] = useState(false);
  const [signForm, setSignForm] = useState({ name: "", cpf: "" });

  // Helper to fix Google Drive image links
  const getImageUrl = (url) => {
    if (!url) return "";
    const driveMatch = url.match(/\/file\/d\/([a-zA-Z0-9_-]+)/);
    if (driveMatch && driveMatch[1]) {
      return `https://drive.google.com/uc?export=view&id=${driveMatch[1]}`;
    }
    return url;
  };

  const load = async () => {
    setLoading(true);
    try { setP(await db.publicSelectOne("propostas", id)); }
    catch { setP(null); }
    setLoading(false);
  };
  useEffect(() => { load(); }, [id]);

  const aceitar = async () => {
    try {
      await db.publicUpdateStatus("propostas", id, "aceita");
      setP({ ...p, status: "aceita" });
    } catch (err) {
      console.error(err);
    }
    setShowContract(true);
  };
  const assinar = async () => {
    if (!signForm.name || !signForm.cpf) return toast.error("Preencha nome e CPF para assinar.");
    setSigning(true);
    try {
      await db.publicSignProposal(id, { signature_name: signForm.name, signature_cpf: signForm.cpf });
      setActed(true); setShowContract(false);
      setP({ ...p, status: "assinada", signature_name: signForm.name });
    } catch (err) { toast.error(err.message); }
    setSigning(false);
  };
  const recusar = async () => {
    try {
      await db.publicUpdateStatus("propostas", id, "recusada");
      setActed(true);
      setP({ ...p, status: "recusada" });
    } catch (err) { toast.error(err.message); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center font-serif text-2xl bg-[#F6F5F0]">Carregando…</div>;
  if (!p) return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-[#F6F5F0] p-6 text-center">
      <h1 className="font-serif text-4xl mb-2">Proposta não encontrada</h1>
      <p className="text-[#4A4A4A]">O link pode ter expirado ou está incorreto.</p>
    </div>
  );

  const isAccepted = p.status === "aceita" || p.status === "assinada";
  const isSigned = p.status === "assinada";
  const isRefused = p.status === "recusada";

  return (
    <div className="min-h-screen bg-[#F6F5F0] py-12 px-6">
      <div className="max-w-3xl mx-auto">
        <div className="flex items-center justify-between border-b-2 border-[#1A1A1A] pb-5 mb-10">
          <div className="flex items-center gap-4">
            {p.company?.logo_url ? (
              <img src={getImageUrl(p.company.logo_url)} alt="Logo da Empresa" className="h-10 w-auto object-contain" />
            ) : (
              <div className="flex items-center gap-2">
                <PenLine className="w-5 h-5 text-[#D9381E]" strokeWidth={2.5} />
                <span className="font-serif text-2xl font-bold">{p.company?.name || "RevisaPro"}</span>
              </div>
            )}
          </div>
          <div className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] text-right">
            Proposta<br />{new Date(p.created_at).toLocaleDateString("pt-BR")}
          </div>
        </div>

        <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#D9381E] mb-3">Proposta Comercial</div>
        <h1 className="font-serif text-5xl md:text-6xl mb-10 leading-[0.95]">
          Para {p.cliente}
        </h1>

        {p.detalhamento && (
          <div className="mb-12">
            <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-4 border-b border-[#D9381E]/20 pb-2">Descrição do Serviço</div>
            <div className="prose prose-stone max-w-none font-serif text-lg leading-relaxed text-[#1A1A1A] whitespace-pre-wrap">
              {p.detalhamento}
            </div>
          </div>
        )}

        <Card className="bg-white border border-black/10 rounded-none p-8 mb-12">
          <dl className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-5 items-start">
            <div>
              <dt className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-1">Serviço</dt>
              <dd className="font-serif text-xl">{p.servico || "—"}</dd>
            </div>
            <div>
              <dt className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-1">Prazo</dt>
              <dd className="font-serif text-xl">{p.prazo || "—"}</dd>
            </div>
            <div>
              <dt className="font-mono text-xs uppercase tracking-widest text-[#8A8A8A] mb-1">Investimento</dt>
              <dd className="font-serif text-4xl text-[#D9381E]">{p.valor ? fmtBRL(p.valor) : "A combinar"}</dd>
            </div>
          </dl>
        </Card>
        
        {p.detalhamento && (
          <div className="mb-12">
            <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-6 flex items-center gap-2">
              <Sparkles className="w-4 h-4" /> Escopo do Serviço
            </div>
            <div className="prose prose-sm md:prose-base max-w-none text-[#4A4A4A] font-serif leading-relaxed whitespace-pre-wrap">
              {p.detalhamento}
            </div>
          </div>
        )}

        {showContract && !isSigned && (
          <div className="bg-white p-8 rounded-none border border-black/10 mb-8 shadow-sm">
            <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-6 flex items-center gap-2">
              <FileSignature className="w-4 h-4" /> Termo de Aceite
            </div>
            
            <div className="prose prose-sm max-w-none text-[#4A4A4A] mb-8 font-serif leading-relaxed">
              <p>Pelo presente instrumento, o cliente <strong>{p.cliente}</strong> formaliza o aceite da proposta comercial referente à prestação de serviços de <strong>{p.servico || "revisão"}</strong>.</p>
              <p>O prazo estabelecido para execução é de <strong>{p.prazo || "conforme alinhamento"}</strong>, mediante o investimento acordado de <strong>{p.valor || "A combinar"}</strong>.</p>
              <p>Ao assinar digitalmente abaixo, ambas as partes concordam com o escopo e as condições apresentadas nesta proposta.</p>
            </div>

            <div className="bg-[#F6F5F0] p-6 border border-black/5">
              <div className="font-mono text-xs uppercase tracking-widest text-[#1A1A1A] mb-4">Assinatura Digital</div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
                <div>
                  <Label className="text-xs font-mono uppercase text-[#8A8A8A]">Nome Completo</Label>
                  <Input 
                    value={signForm.name} 
                    onChange={(e) => setSignForm({ ...signForm, name: e.target.value })} 
                    className="rounded-none mt-1 border-black/20 text-base" 
                    placeholder="Seu nome completo" 
                  />
                </div>
                <div>
                  <Label className="text-xs font-mono uppercase text-[#8A8A8A]">CPF / CNPJ</Label>
                  <Input 
                    value={signForm.cpf} 
                    onChange={(e) => setSignForm({ ...signForm, cpf: e.target.value })} 
                    className="rounded-none mt-1 border-black/20 text-base" 
                    placeholder="000.000.000-00" 
                  />
                </div>
              </div>
              <div className="flex flex-wrap gap-3">
                <Button onClick={assinar} disabled={signing} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white px-8">
                  {signing ? "Processando..." : "Assinar Eletronicamente"}
                </Button>
                <Button onClick={() => setShowContract(false)} variant="outline" className="rounded-none">
                  Cancelar
                </Button>
              </div>
            </div>
          </div>
        )}

        {!acted && !isAccepted && !isRefused && !showContract && (
          <div className="bg-[#1A1A1A] text-white p-8 rounded-none">
            <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-3">Sua decisão</div>
            <h2 className="font-serif text-2xl mb-6">Vamos seguir com este trabalho?</h2>
            <div className="flex flex-wrap gap-3">
              <Button onClick={aceitar} className="rounded-none bg-[#D9381E] hover:bg-[#B82914] text-white px-8 py-6" data-testid="public-accept-btn">
                <Check className="w-4 h-4 mr-2" /> Aceitar proposta
              </Button>
              <Button onClick={recusar} variant="outline" className="rounded-none bg-transparent border-white/30 text-white hover:bg-white hover:text-[#1A1A1A] px-8 py-6">
                <X className="w-4 h-4 mr-2" /> Recusar
              </Button>
            </div>
          </div>
        )}

        {(isAccepted || acted) && isSigned && (
          <Card className="bg-[#1A1A1A] text-white p-8 rounded-none border-0">
            <div className="font-mono text-xs uppercase tracking-widest text-[#D9381E] mb-3 flex items-center gap-2">
              <Check className="w-4 h-4" /> Proposta e Contrato Assinados
            </div>
            <h2 className="font-serif text-3xl mb-4">Obrigada pela confiança, {p.signature_name || p.cliente}!</h2>
            <p className="text-white/80 mb-4">A revisora foi notificada e entrará em contato em breve com os próximos passos do projeto.</p>
          </Card>
        )}

        {p.status === "recusada" && (
          <Card className="bg-white border border-black/10 p-8 rounded-none">
            <h2 className="font-serif text-2xl mb-2">Proposta recusada</h2>
            <p className="text-[#4A4A4A]">Sem problemas. A revisora foi notificada.</p>
          </Card>
        )}

        <div className="mt-12 pt-6 border-t border-black/10 font-mono text-xs uppercase tracking-widest text-[#8A8A8A] text-center flex flex-col gap-2 items-center">
          <div>{p.company?.name || "RevisaPro"} · Proposta {String(p.id).slice(0, 8)}</div>
          {p.company?.cnpj && <div>CNPJ/CPF: {p.company.cnpj}</div>}
        </div>
      </div>
    </div>
  );
}
